import deepmerge from 'deepmerge'
import {
  Experience,
  PaymentPolicyType,
  Mode,
  NodeType,
  LayoutType,
  Orientation,
  Crop,
  ThumbnailsAlignment,
  Recurrence,
  DayOfWeek,
} from '@wix/ambassador-table-reservations-v1-experience/types'
import {DEFAULT_CURRENCY} from '../../utils/constants'

import {EDITOR_MOCK_RESERVATION_LOCATION_ID} from './reservationLocation'

const DEFAULT_IMAGE_DIMENSION = 1024

type GalleryImage = {id: string; width?: number; height?: number}

const buildRichContent = (bodyText: string, gallery: GalleryImage[]) => {
  const paragraphs = bodyText.split('\n\n').map((chunk, idx) => ({
    type: NodeType.PARAGRAPH,
    id: `p-${idx}`,
    nodes: [
      {
        type: NodeType.TEXT,
        id: '',
        nodes: [],
        textData: {
          text: chunk,
          decorations: [],
        },
      },
    ],
    paragraphData: {},
  }))

  return {
    nodes: [
      ...paragraphs,
      {
        type: NodeType.PARAGRAPH,
        id: 'spacer-pre-gallery',
        nodes: [],
        paragraphData: {},
      },
      {
        type: NodeType.GALLERY,
        id: 'gallery',
        nodes: [],
        galleryData: {
          items: gallery.map(({id, width, height}) => ({
            image: {
              media: {
                src: {url: id},
                width: width ?? DEFAULT_IMAGE_DIMENSION,
                height: height ?? DEFAULT_IMAGE_DIMENSION,
              },
            },
          })),
          options: {
            layout: {
              type: LayoutType.GRID,
              horizontalScroll: false,
              orientation: Orientation.COLUMNS,
              numberOfColumns: 3,
            },
            item: {
              ratio: 1,
              crop: Crop.FILL,
            },
            thumbnails: {
              placement: ThumbnailsAlignment.NONE,
            },
          },
        },
      },
      {
        type: NodeType.PARAGRAPH,
        id: 'spacer-post-gallery',
        nodes: [],
        paragraphData: {},
      },
    ],
    documentStyle: {},
  }
}

type T = (key: string) => string

export const buildExperiencesMock = (t: T): Experience[] => {
  const defaultExperience: Experience = {
    id: 'editor-mock-experience-id',
    reservationLocationId: EDITOR_MOCK_RESERVATION_LOCATION_ID,
    currency: DEFAULT_CURRENCY,
    configuration: {
      displayInfo: {
        name: 'name',
        shortDescription: 'shortDescription',
        coverImage: undefined,
        richContent: undefined,
      },
      paymentPolicy: {
        paymentPolicyType: PaymentPolicyType.FREE,
      },
      onlineReservations: {
        partySize: {
          min: 1,
          max: 6,
        },
        approval: {
          mode: Mode.AUTOMATIC,
        },
        businessSchedule: undefined,
      },
      visible: true,
    },
  }

  const experiences: Partial<Experience>[] = [
    {
      id: 'editor-mock-experience-id-1',
      configuration: {
        displayInfo: {
          name: t('uou-experiences.editor-mock.experience-1.name'),
          shortDescription: t('uou-experiences.editor-mock.experience-1.short-description'),
          coverImage: {
            id: 'bfc5fb_746a36de54ac479aab802ef4b879e062~mv2.png',
            url: '',
            height: DEFAULT_IMAGE_DIMENSION,
            width: DEFAULT_IMAGE_DIMENSION,
          },
          richContent: buildRichContent(
            t('uou-experiences.editor-mock.experience-1.rich-content'),
            [
              {id: 'bfc5fb_a41cbb78f05b4ee4af267116c94aace9~mv2.png'},
              {id: 'bfc5fb_255e486883ea4864a4e537ab4ef48c5d~mv2.png'},
              {id: 'bfc5fb_5bf3c655b6824aa0943d5832213952f0~mv2.png'},
            ],
          ),
        },
        paymentPolicy: {
          paymentPolicyType: PaymentPolicyType.PER_GUEST,
          perGuestOptions: {
            price: '10',
          },
        },
      },
    },
    {
      id: 'editor-mock-experience-id-2',
      configuration: {
        displayInfo: {
          name: t('uou-experiences.editor-mock.experience-2.name'),
          shortDescription: t('uou-experiences.editor-mock.experience-2.short-description'),
          coverImage: {
            id: 'bfc5fb_fc76f8a6350443088e78a7dccd5f3f72~mv2.png',
            url: '',
            height: DEFAULT_IMAGE_DIMENSION,
            width: DEFAULT_IMAGE_DIMENSION,
          },
          richContent: buildRichContent(
            t('uou-experiences.editor-mock.experience-2.rich-content'),
            [
              {id: 'bfc5fb_dbcc1246ab004de687056c6e5f208109~mv2.png'},
              {id: 'bfc5fb_6bd77067b53f497d82dc1df3a755506d~mv2.png'},
              {id: 'bfc5fb_2bdb9abb9f1645bfb9020492f35256f6~mv2.png'},
            ],
          ),
        },
        onlineReservations: {
          businessSchedule: {
            durationInMinutes: 120,
            entries: [
              {
                recurrence: Recurrence.WEEKLY,
                weeklyOptions: {
                  startDate: '2027-07-19',
                  startDaysAndTimes: [
                    {
                      day: DayOfWeek.MONDAY,
                      time: '12:00',
                    },
                    {
                      day: DayOfWeek.TUESDAY,
                      time: '12:00',
                    },
                  ],
                },
              },
            ],
          },
        },
      },
    },
    {
      id: 'editor-mock-experience-id-3',
      configuration: {
        displayInfo: {
          name: t('uou-experiences.editor-mock.experience-3.name'),
          shortDescription: t('uou-experiences.editor-mock.experience-3.short-description'),
          coverImage: {
            id: 'bfc5fb_31ec741c733c44b193dd90ebe6a4fb3e~mv2.png',
            url: '',
            height: DEFAULT_IMAGE_DIMENSION,
            width: DEFAULT_IMAGE_DIMENSION,
          },
          richContent: buildRichContent(
            t('uou-experiences.editor-mock.experience-3.rich-content'),
            [
              {id: 'bfc5fb_5d070dcf98114370a28fb9a7f5c94337~mv2.png'},
              {id: 'bfc5fb_49d2cfd4b99f48a982c5e8c304e08671~mv2.png'},
              {id: 'bfc5fb_454ff5a941674067ba0c07a1a21a673b~mv2.png'},
            ],
          ),
        },
        paymentPolicy: {
          paymentPolicyType: PaymentPolicyType.PER_GUEST,
          perGuestOptions: {
            price: '10',
          },
        },
        onlineReservations: {
          businessSchedule: {
            durationInMinutes: 120,
            entries: [
              {
                recurrence: Recurrence.ONE_TIME,
                oneTimeOptions: {
                  startDate: '2027-07-19',
                  startTime: '17:00',
                },
              },
            ],
          },
        },
      },
    },
  ]

  return experiences.map((experience) => deepmerge(defaultExperience, experience))
}
